import { CpmBadge, CpmCheckbox } from '../../../components/design-system';
import type { CatalogListRow } from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import { canTurnOn, groupByCategory, shortReportId } from '../helpers';
import CategoryGroupHeader from './CategoryGroupHeader';
import CopyIdButton from './CopyIdButton';
import InfoI from './InfoI';
import KindBadge from './KindBadge';

interface ReportTickListProps {
    rows: CatalogListRow[];
    selectedIds: string[];
    onToggle: (reportID: string) => void;
    disabled?: boolean;
    search?: string;
    layout?: 'compact' | 'create';
}

function lockReason(row: CatalogListRow): string | null {
    if (row.kind === 'ALWAYS_ON') return REPORTS_TIPS.KIND_ALWAYS_ON.body;
    if (row.kind === 'DEAD' || !row.generatorBound) {
        return 'No builder linked, so it cannot be ticked in — it would turn nothing on. Taking one out is always allowed.';
    }
    if (row.activeStatus === 'Inactive') {
        return 'Inactive in the catalogue, so it cannot be ticked in. Taking one out is always allowed.';
    }
    return null;
}

function membership(checked: boolean, locked: boolean) {
    if (locked) return { label: 'Cannot go in a template', tone: 'warning' as const };
    if (checked) return { label: 'Will go in', tone: 'brand' as const };
    return { label: 'Not in', tone: 'neutral' as const };
}

export default function ReportTickList({
    rows,
    selectedIds,
    onToggle,
    disabled = false,
    search = '',
    layout = 'compact',
}: ReportTickListProps) {
    const q = search.trim().toLowerCase();
    const visible = q
        ? rows.filter(
              (row) =>
                  row.name.toLowerCase().includes(q) ||
                  (row.description ?? '').toLowerCase().includes(q)
          )
        : rows;
    const groups = groupByCategory(visible);
    const inCount = selectedIds.length;
    const create = layout === 'create';

    return (
        <div
            className="overflow-auto"
            style={{
                maxHeight: 400,
                border: '1px solid var(--cpm-border-default)',
                borderRadius: 10,
            }}
        >
            {groups.map((group) => {
                const inGroup = group.rows.filter((row) =>
                    selectedIds.includes(row.reportID)
                ).length;
                return (
                    <div key={group.name}>
                        <CategoryGroupHeader
                            variant="sticky"
                            name={group.name}
                            count={group.rows.length}
                            inCountLabel={create ? `${inGroup} in this template` : `${inGroup} in`}
                        />
                        {group.rows.map((row) => {
                            const checked = selectedIds.includes(row.reportID);
                            const allowed = canTurnOn(row);
                            const leftover = checked && !allowed;
                            const locked = !allowed && !leftover;
                            const reason = lockReason(row);
                            const badge = membership(checked, locked);
                            const meta = row.allStates
                                ? REPORTS_COPY.ALL_STATES
                                : (row.states ?? []).join(' · ');
                            return (
                                <div
                                    key={row.reportID}
                                    className="items-center"
                                    style={{
                                        display: create ? 'grid' : 'flex',
                                        gridTemplateColumns: create ? '26px 1fr 170px' : undefined,
                                        gap: create ? 12 : 10,
                                        padding: create
                                            ? '10px 14px 10px 18px'
                                            : '9px 14px 9px 16px',
                                        borderBottom: '1px solid var(--cpm-border-default)',
                                        cursor: locked || disabled ? 'default' : 'pointer',
                                    }}
                                    onClick={() => {
                                        if (locked || disabled) return;
                                        onToggle(row.reportID);
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background =
                                            'var(--cpm-purple-tint-3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                    }}
                                >
                                    {locked ? (
                                        <span className="inline-flex" title={reason ?? undefined}>
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="var(--cpm-text-tertiary)"
                                                strokeWidth="2"
                                            >
                                                <rect x="5" y="11" width="14" height="10" rx="2" />
                                                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                                            </svg>
                                        </span>
                                    ) : (
                                        <span
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex"
                                        >
                                            <CpmCheckbox
                                                checked={checked}
                                                disabled={disabled}
                                                onChange={() => onToggle(row.reportID)}
                                            />
                                        </span>
                                    )}
                                    <div className="min-w-0">
                                        <div
                                            style={{
                                                fontSize: create ? 12.5 : 13.5,
                                                fontWeight: 800,
                                                letterSpacing: '-0.005em',
                                            }}
                                        >
                                            {row.name}
                                        </div>
                                        <div
                                            className="flex items-center flex-wrap"
                                            style={{ gap: 7, marginTop: 3 }}
                                        >
                                            <span
                                                style={{
                                                    fontFamily: 'var(--cpm-font-mono)',
                                                    fontSize: create ? 10 : 10.5,
                                                    fontWeight: 700,
                                                    color: 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                {shortReportId(row.reportID)}
                                            </span>
                                            <CopyIdButton id={row.reportID} />
                                            {create && meta ? (
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        color: 'var(--cpm-text-secondary)',
                                                    }}
                                                >
                                                    {meta}
                                                </span>
                                            ) : null}
                                            {create ? <KindBadge kind={row.kind} /> : null}
                                            {!create && row.alreadyIn ? (
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        fontWeight: 800,
                                                        color: 'var(--cpm-success-text)',
                                                    }}
                                                >
                                                    {REPORTS_COPY.ALREADY_IN_TEMPLATE}
                                                </span>
                                            ) : null}
                                        </div>
                                        {create && reason && locked ? (
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    lineHeight: 1.45,
                                                    color: 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                {reason}
                                            </div>
                                        ) : null}
                                    </div>
                                    {create ? (
                                        <div className="flex items-center justify-end">
                                            <CpmBadge tone={badge.tone}>{badge.label}</CpmBadge>
                                        </div>
                                    ) : (
                                        <>
                                            <KindBadge kind={row.kind} />
                                            {reason && !allowed ? (
                                                <InfoI
                                                    title={REPORTS_TIPS.NOT_IN_TEMPLATE.title}
                                                    body={reason}
                                                />
                                            ) : null}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
            {visible.length === 0 ? (
                <p
                    className="m-0"
                    style={{
                        padding: 20,
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {q ? REPORTS_COPY.FILTERED_EMPTY : 'No reports to tick.'}
                </p>
            ) : create ? null : (
                <p
                    className="m-0 text-center"
                    style={{
                        padding: 10,
                        fontSize: 11.5,
                        fontWeight: 800,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {inCount} ticked
                </p>
            )}
        </div>
    );
}
